// PROVENANCE VIEW for one agent operation — the flowchart plus a details panel.
//
// THIS VIEW NEVER ANIMATES A LIVE RUN. That is the whole point of the rework.
// Progress is now a compact strip inside the chat thread; when the run
// finishes it posts a RESULT into the thread ("50 leads → AI Agent -
// 2026-08-08 14:02"), and this view is what opens when someone clicks that
// result — or one lead inside it. It answers a question about the PAST: how was
// this output arrived at, and (in lead mode) why did THIS lead survive? There is
// consequently no `running` prop, no spinner, no marching wire, no "Working…"
// chip: a picture that animates as though work were happening would be a
// picture of something that is not occurring.
//
// PROP CONTRACT
//   operation       { id, label, blurb } — the operation this run was
//   passes          the run's nodes, each carrying dependsOn/lane/status/
//                   headline/detail/metric/startedAt/endedAt
//   result          the run's return value, or null if it stopped short
//   lead            OPTIONAL { key, name } — open the view for ONE lead and it
//                   becomes that lead's story: its path is highlighted, every
//                   node it never reached is dimmed, and each node it did reach
//                   shows the verdict the run recorded for it
//   onBack, onRestart — callbacks out
//
// Purely presentational. It never imports lib/agentRun.js and never starts
// anything, so it renders equally from live state or a page reload that only
// has the passes.
//
// TRUTH RULE. Everything shown here is read off what the run RECORDED — the
// pass objects, and result.trace for the per-lead verdicts. Nothing is
// re-derived after the fact. When the run recorded nothing for a lead at a
// node, the panel says so; it does not guess a verdict that could contradict
// the run, and it does not dim a path it cannot vouch for.
//
// ACCESSIBILITY. The canvas nodes are real buttons (see FlowCanvas) with
// labels spelling out position, status and per-lead verdict, in emission order,
// arrow-key reachable — so the flowchart is no longer aria-hidden and no longer
// needs a shadow list beside it. One live region announces the run's outcome
// once, on arrival. Motion respects prefers-reduced-motion (FlowCanvas).
import { useMemo, useState } from 'react'
import FlowCanvas, {
  BADGE_TEXT,
  BADGE_TONE,
  StatusMark,
  elapsedLabel,
  passIcon,
} from './FlowCanvas.jsx'
import { ArrowLeftIcon, CheckIcon, CloseIcon, RefreshIcon } from './icons.jsx'

const SETTLED = new Set(['done', 'skipped', 'failed'])
// A pass abandon() marked 'skipped' never ran — it was cut off when an earlier
// pass ended the run. Counting it as settled made a run that died on step 1
// report "10 of 10 steps": a reader was told the run was 100% complete when it
// had wholly failed. A pass that legitimately had nothing to do (enrichment
// already present, CRM unreachable) still counts.
const isSettled = (p) => SETTLED.has(p?.status) && !p?.abandoned

// ── per-lead provenance ─────────────────────────────────────────────────────
//
// The shape the engine records (lib/agentRun.js, makeTracer):
//   result.trace = { [leadKey]: [{ nodeId, verdict, note }, …] }   in run order
//   result.traceOmitted = how many leads' traces the snapshot had to shed
// leadKey is keyOf() — the same key the tables and lists already hold, so the
// `lead` prop's key needs no translation.
//
// Other spellings are tolerated — a Map, a { byLead } / { leads } wrapper, an
// array of { key, steps }, steps as an object map, a bare string read as the
// verdict — because a receipt must not go blank over a container type. What is
// NOT tolerated is inventing an entry: the result is filtered down to ids that
// exist in this run's passes, and a lead with nothing recorded yields null,
// which the UI renders as "not recorded" rather than as a path. A node that
// never saw a lead deliberately wrote nothing for it, and that silence is the
// evidence the lead never got that far.

function traceRoot(result) {
  const t = result?.trace
  if (!t) return null
  if (t instanceof Map || Array.isArray(t)) return t
  if (Array.isArray(t.leads)) return t.leads
  if (t.byLead) return t.byLead
  return t
}

function leadEntry(root, key) {
  if (!root || !key) return null
  if (root instanceof Map) return root.get(key) ?? null
  if (Array.isArray(root)) {
    return root.find((e) => e && (e.key === key || e.leadKey === key || e.id === key)) || null
  }
  return root[key] ?? null
}

function normalizeStep(v) {
  if (v == null) return null
  if (typeof v === 'string') return { verdict: v.trim(), note: '' }
  if (typeof v !== 'object') return null
  const verdict = String(v.verdict ?? v.result ?? v.outcome ?? v.state ?? '').trim()
  const note = String(v.note ?? v.reason ?? v.detail ?? v.why ?? '').trim()
  if (!verdict && !note) return null
  return { verdict, note }
}

/**
 * @returns {Map<string, {verdict, note}>|null} what the run recorded for this
 * lead, keyed by node id and filtered to nodes this run actually has. null when
 * nothing was recorded — "we do not know", not "it passed nothing".
 */
function leadTrace(result, lead, passes) {
  if (!lead?.key) return null
  const entry = leadEntry(traceRoot(result), lead.key)
  if (!entry) return null
  const known = new Set(passes.map((p) => p.id))
  const out = new Map()

  const put = (id, raw) => {
    if (!known.has(id)) return
    const step = normalizeStep(raw)
    if (step) out.set(id, step)
  }

  // A wrapper key only counts when it actually holds a list or map of steps —
  // otherwise the entry IS the map, and a node that happened to be called
  // "steps" would hijack the whole lookup.
  const wrapped = [entry.steps, entry.nodes, entry.passes, entry.trace].find(
    (v) => Array.isArray(v) || v instanceof Map || (v && typeof v === 'object'),
  )
  const steps = Array.isArray(entry) ? entry : wrapped || entry

  if (Array.isArray(steps)) {
    steps.forEach((s) => put(String(s?.id ?? s?.nodeId ?? s?.pass ?? ''), s))
  } else if (steps instanceof Map) {
    steps.forEach((v, k) => put(String(k), v))
  } else if (steps && typeof steps === 'object') {
    Object.entries(steps).forEach(([k, v]) => put(String(k), v))
  }

  return out.size ? out : null
}

// The engine writes exactly three verdicts, and they must NOT be collapsed:
//   kept    — this node judged the lead and it carried on
//   dropped — this node is the reason the lead is not in the output
//   skipped — this node could not judge it (a system was unreadable, or the
//             lead sat outside a bounded window). An admission, never a
//             finding, so it is styled quiet-neutral and never red: painting a
//             degraded HubSpot read as a rejection would be a lie about why the
//             lead is where it is.
const VERDICT_CLASS = {
  kept: 'bg-primary-tint text-primary-dark',
  dropped: 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.2)] dark:text-[#fca5a5]',
  skipped: 'bg-panel text-dim',
  neutral: 'bg-panel text-dim',
}

const VERDICT_GLOSS = {
  kept: 'This step looked at the lead and kept it.',
  dropped: 'This step is the reason the lead is not in the output.',
  skipped: 'This step could not judge the lead, so it left it alone.',
}

const verdictTone = (verdict) => (VERDICT_CLASS[verdict] ? verdict : 'neutral')

// ── small panel primitives ──────────────────────────────────────────────────

function PanelLabel({ children }) {
  return <div className="mb-[6px] text-[12px] font-semibold text-dim">{children}</div>
}

function PanelBlock({ label, children, className = '' }) {
  return (
    <div className={`mt-[14px] first:mt-0 ${className}`}>
      <PanelLabel>{label}</PanelLabel>
      {children}
    </div>
  )
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex flex-none items-center gap-x-1 rounded-full px-[7px] py-px text-[10px] font-bold uppercase tracking-[0.04em] ${
        BADGE_TONE[status] || BADGE_TONE.pending
      }`}
    >
      <StatusMark status={status} />
      {BADGE_TEXT[status] || BADGE_TEXT.pending}
    </span>
  )
}

// ── the details panel ───────────────────────────────────────────────────────
//
// Geometry, type scale and tokens are the app's modal scale: a 16px-radius
// surface, a header at px-5 py-[18px] with a 17px title and a × to close, a p-5
// body whose labels are 12px semibold dim and whose values are 13–14px ink, and
// a bordered footer at px-5 py-[14px] holding the actions. Every surface should
// feel like one product; the only difference is that this one is docked rather than
// modal, because the flow beside it is the context.

function NodeDetails({ node, step, total, deps, lead, verdict }) {
  const status = node.status || 'pending'
  const took = elapsedLabel(node)
  const details = node.detail || []
  const metric = node.metric

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <StatusBadge status={status} />
        <span className="text-[12px] text-faint">
          Step {step} of {total}
        </span>
        {took && <span className="text-[12px] tabular-nums text-faint">· {took}</span>}
      </div>

      {/* In lead mode this is the reason the panel was opened, so it leads. */}
      {lead && (
        <PanelBlock label={`For ${lead.name || 'this lead'}`}>
          {verdict ? (
            <>
              {verdict.verdict && (
                <span
                  className={`inline-flex items-center rounded-full px-[9px] py-[3px] text-[12px] font-semibold ${
                    VERDICT_CLASS[verdictTone(verdict.verdict)]
                  }`}
                >
                  {verdict.verdict}
                </span>
              )}
              {verdict.note && (
                <p className="m-0 mt-2 text-[13px] leading-[1.5] text-ink">{verdict.note}</p>
              )}
              {VERDICT_GLOSS[verdict.verdict] && (
                <p className="m-0 mt-1.5 text-[12px] leading-[1.5] text-faint">
                  {VERDICT_GLOSS[verdict.verdict]}
                </p>
              )}
            </>
          ) : (
            <p className="m-0 rounded-[9px] bg-panel px-3 py-[9px] text-[12.5px] leading-[1.5] text-dim">
              The run recorded nothing for this lead here — either it never reached this step, or
              this step does not judge leads one by one. Nothing is inferred either way.
            </p>
          )}
        </PanelBlock>
      )}

      <PanelBlock label="What this step did">
        <p className="m-0 text-[14px] leading-[1.5] text-ink">
          {node.headline || node.subtitle || <span className="text-faint">Nothing recorded.</span>}
        </p>
      </PanelBlock>

      {metric && (
        <PanelBlock label="Result">
          <div className="inline-flex items-baseline gap-x-[7px] rounded-[10px] bg-panel px-3 py-[9px]">
            <span className="text-[19px] font-bold tabular-nums leading-none text-ink">
              {Number(metric.value).toLocaleString()}
            </span>
            <span className="text-[12.5px] text-dim">{metric.label}</span>
          </div>
        </PanelBlock>
      )}

      {details.length > 0 && (
        <PanelBlock label="How it decided">
          <ul className="m-0 flex list-none flex-col gap-y-[7px] p-0">
            {details.map((line, i) => (
              <li key={`${line}-${i}`} className="flex items-start gap-x-[9px]">
                <span
                  aria-hidden="true"
                  className={`mt-[7px] h-[4px] w-[4px] flex-none rounded-full ${
                    status === 'failed' ? 'bg-danger' : 'bg-line'
                  }`}
                />
                <span className="text-[13px] leading-[1.5] text-dim">{line}</span>
              </li>
            ))}
          </ul>
        </PanelBlock>
      )}

      <PanelBlock label="Ran after">
        <p className="m-0 text-[13px] leading-[1.5] text-dim">
          {deps.length ? deps.join(', ') : 'Nothing — this step opens the run.'}
        </p>
      </PanelBlock>
    </>
  )
}

function RunSummary({
  operation,
  total,
  settledCount,
  finished,
  stopped,
  nothingToDo,
  failed,
  stopper,
  result,
  selectedCount,
  totalElapsed,
  lead,
  trace,
}) {
  const reached = trace ? trace.size : 0
  const omitted = Number(result?.traceOmitted) || 0

  return (
    <>
      <PanelBlock label="Outcome">
        <p className="m-0 text-[14px] leading-[1.5] text-ink">
          {finished
            ? 'The run completed and staged its work as a draft.'
            : nothingToDo
              ? 'The run completed correctly — there was nothing to do, so nothing was built.'
              : stopped
                ? `The run stopped at “${stopper?.title || 'an early step'}”.`
                : 'This run has no recorded outcome.'}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-faint">
          <span className="tabular-nums">
            {settledCount} of {total} steps finished
          </span>
          {totalElapsed && <span className="tabular-nums">· {totalElapsed}</span>}
          {stopped && (
            <span
              className={`rounded-full px-[7px] py-px text-[10px] font-bold uppercase tracking-[0.04em] ${
                failed
                  ? 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.2)] dark:text-[#fca5a5]'
                  : 'bg-panel text-dim'
              }`}
            >
              {failed ? 'Failed' : 'Nothing downstream'}
            </span>
          )}
        </div>
      </PanelBlock>

      {lead && (
        <PanelBlock label={`This view is scoped to ${lead.name || 'one lead'}`}>
          <p className="m-0 text-[13px] leading-[1.5] text-dim">
            {trace
              ? `The run recorded this lead at ${reached} of ${total} step${total === 1 ? '' : 's'}. Its path is drawn solid; everything it never reached is dimmed. Select a step for the verdict recorded there.`
              : 'The run recorded no per-step trace for this lead, so no path is claimed — the whole graph is shown as it ran.'}
          </p>
          {!trace && omitted > 0 && (
            <p className="m-0 mt-1.5 text-[12px] leading-[1.5] text-faint">
              This run kept traces for the leads it selected plus the highest-ranked of the rest;{' '}
              {omitted.toLocaleString()} lead{omitted === 1 ? "'s trace was" : "s' traces were"} shed
              to keep the saved run small.
            </p>
          )}
        </PanelBlock>
      )}


      {/* The reassurance is a first-class element, not fine print: the whole
          point of staging a draft is that a human decides. */}
      <p className="m-0 mt-[14px] rounded-[10px] bg-panel px-3 py-2.5 text-[12.5px] leading-[1.55] text-dim">
        <span className="font-semibold text-ink">Nothing was sent.</span> No email left your account —
        anything this run staged is a draft waiting for your review.
      </p>

      <p className="m-0 mt-[14px] text-[12.5px] leading-[1.5] text-faint">
        Select any step in the flow to see what it did and how it decided.
        {operation?.blurb ? ` ${operation.blurb}` : ''}
      </p>
    </>
  )
}

export default function AgentRunView({
  operation,
  passes = [],
  result = null,
  lead = null,
  onBack,
  onRestart,
}) {
  const [selectedId, setSelectedId] = useState('')

  const total = passes.length
  const settledCount = passes.filter(isSettled).length
  const failed = passes.find((p) => p.status === 'failed') || null

  // A run that abandoned partway still returns a result object (carrying the
  // passes and whatever it managed to keep), so presence alone must not be read
  // as success — `ok:false` means the pipeline stopped and there is no list or
  // draft to link to. A run that found nothing to do completed correctly, it
  // just built nothing.
  const nothingToDo = Boolean(result) && result.nothingToDo === true
  const finished = Boolean(result) && result.ok !== false && !nothingToDo
  // Packed receipts store a count; a LIVE result (and every receipt written
  // before that change) carries the array itself.
  const selectedCount = result?.selectedCount ?? result?.selected?.length ?? 0

  // The shape `result` cannot express: a run that abandoned without any pass
  // FAILING. abandon() is how a pass says "there is nothing downstream of me"
  // (no reachable leads, no inbox to read), and Chat keeps `result` null for it
  // — so the terminal state has to be read off the passes instead.
  const started = passes.some((p) => p.status !== 'pending')
  const abandoned = passes.some((p) => p.abandoned)
  const stopped = !finished && !nothingToDo && started && (Boolean(failed) || abandoned)
  // Which pass ended it: the failure if there was one, otherwise the last pass
  // that actually ran (the one that called abandon) — never an abandoned pass,
  // which by definition never ran and has nothing to say.
  const stopper =
    failed ||
    [...passes].reverse().find((p) => p.status !== 'pending' && !p.abandoned) ||
    null

  const terminalLabel = finished
    ? 'End · nothing sent'
    : stopped
      ? 'End · stopped, nothing sent'
      : nothingToDo
        ? 'End · nothing to do'
        : 'End'

  const totalElapsed = useMemo(() => {
    const starts = passes.map((p) => p.startedAt).filter(Boolean)
    const ends = passes.map((p) => p.endedAt).filter(Boolean)
    if (!starts.length || !ends.length) return ''
    return elapsedLabel({ startedAt: Math.min(...starts), endedAt: Math.max(...ends) })
  }, [passes])

  // Per-lead provenance, read once off what the run recorded.
  const trace = useMemo(() => leadTrace(result, lead, passes), [result, lead, passes])
  const reachedIds = useMemo(() => (trace ? new Set(trace.keys()) : null), [trace])
  const verdicts = useMemo(() => {
    if (!trace) return null
    const out = {}
    trace.forEach((v, k) => {
      out[k] = v.verdict || 'recorded'
    })
    return out
  }, [trace])

  const selected = selectedId ? passes.find((p) => p.id === selectedId) || null : null
  const selectedIndex = selected ? passes.indexOf(selected) : -1
  const depTitles = useMemo(() => {
    if (!selected) return []
    const byId = new Map(passes.map((p) => [p.id, p]))
    return (selected.dependsOn || []).map((id) => byId.get(id)?.title || id)
  }, [selected, passes])

  // Announced once on arrival — this is a finished run, so there is no stream
  // of transitions to narrate.
  const announcement = useMemo(() => {
    const scope = lead?.name ? ` Showing how ${lead.name} got here.` : ''
    if (!total) return ''
    if (finished)
      return `Provenance for ${operation?.label || 'this run'}. ${settledCount} of ${total} steps finished. Nothing was sent.${scope}`
    if (nothingToDo)
      return `Provenance for ${operation?.label || 'this run'}. There was nothing to do. Nothing was sent.${scope}`
    if (stopped)
      return `Provenance for ${operation?.label || 'this run'}. The run stopped at ${stopper?.title || 'an early step'}. Nothing was sent.${scope}`
    return `Provenance for ${operation?.label || 'this run'}. ${settledCount} of ${total} steps finished.${scope}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.label, finished, nothingToDo, stopped, settledCount, total, lead?.name])

  const SelectedIcon = selected ? passIcon(selected.id) : null

  return (
    <section className="flex h-full min-h-0 flex-col">
      {/* ── header ────────────────────────────────────────────────────── */}
      <header className="flex-none">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to chat"
          className="inline-flex cursor-pointer items-center gap-x-2 rounded-[8px] bg-transparent px-1 py-1 text-[13px] font-medium text-dim transition-colors hover:text-ink [border:none]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back</span>
        </button>

        <div className="mt-2 flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
          <div className="min-w-0">
            <h1 className="m-0 text-[21px] font-bold tracking-[-0.02em] text-ink">
              {operation?.label || 'Agent run'}
            </h1>
            <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] leading-[1.55] text-dim">
              <span>
                {lead?.name
                  ? `How ${lead.name} ended up in this result.`
                  : 'How this result was produced.'}
              </span>
              {lead?.name && (
                <span className="rounded-full bg-primary-tint px-[8px] py-px text-[11px] font-semibold text-primary-dark">
                  {lead.name}
                </span>
              )}
            </p>
          </div>

          {total > 0 && (
            <div className="flex flex-none flex-col items-end gap-y-2">
              <span className="text-[12.5px] font-semibold tabular-nums text-dim" aria-hidden="true">
                {settledCount} of {total} steps
              </span>
              {/* Same segmented bar the wizard uses, so completeness reads the
                  same way everywhere in the app. */}
              <div
                className="flex w-[170px] gap-x-[7px]"
                role="progressbar"
                aria-valuenow={settledCount}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-label={`${settledCount} of ${total} steps finished`}
              >
                {passes.map((p, i) => (
                  <span
                    key={p.id || i}
                    className="relative h-[4px] flex-1 overflow-hidden rounded-[2px] bg-line"
                  >
                    <span
                      className={`absolute inset-0 origin-left rounded-[2px] ${
                        p.status === 'failed'
                          ? 'bg-danger'
                          : p.status === 'skipped'
                            ? 'bg-faint'
                            : 'bg-primary'
                      } ${isSettled(p) ? 'scale-x-100' : 'scale-x-0'}`}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">
          This agent selects, researches and drafts. It never sends email — every draft waits for
          your review.
        </p>
      </header>

      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {/* ── canvas + details panel ────────────────────────────────────────
          Side by side on a wide window, stacked on a narrow one. The canvas
          owns its own scrolling; the panel owns its own, so a long node never
          squeezes the flow out of existence. */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <div className="relative min-h-[240px] flex-1">
          <FlowCanvas
            passes={passes}
            terminalLabel={terminalLabel}
            terminalDone={finished || stopped || nothingToDo}
            selectedId={selectedId}
            onSelect={setSelectedId}
            reachedIds={reachedIds}
            verdicts={verdicts}
          />
        </div>

        <aside
          aria-label={selected ? `${selected.title} — step details` : 'Run summary'}
          className="flex max-h-[46%] w-full flex-none flex-col overflow-hidden rounded-[16px] bg-surface shadow-card [border:1px_solid_var(--border)] lg:max-h-none lg:w-[344px]"
        >
          <div className="flex flex-none items-start justify-between gap-x-3 px-5 py-[18px] [border-bottom:1px_solid_var(--border)]">
            <div className="flex min-w-0 items-start gap-x-[11px]">
              {SelectedIcon && (
                <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] bg-primary-tint text-primary [&_svg]:h-[17px] [&_svg]:w-[17px]">
                  <SelectedIcon aria-hidden="true" />
                </span>
              )}
              <div className="min-w-0">
                <h3 className="m-0 text-[17px] leading-[1.25] text-ink">
                  {selected ? selected.title : 'This run'}
                </h3>
                <p className="m-0 mt-[3px] text-[12.5px] leading-[1.4] text-dim">
                  {selected
                    ? selected.subtitle || operation?.label || ''
                    : lead?.name
                      ? `Provenance for ${lead.name}`
                      : 'Provenance'}
                </p>
              </div>
            </div>
            {selected && (
              <button
                type="button"
                onClick={() => setSelectedId('')}
                aria-label="Close step details"
                className="-mt-1 cursor-pointer bg-transparent text-[24px] leading-none text-faint transition-colors hover:text-ink [border:none]"
              >
                ×
              </button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {selected ? (
              <NodeDetails
                node={selected}
                step={selectedIndex + 1}
                total={total}
                deps={depTitles}
                lead={lead}
                verdict={trace ? trace.get(selected.id) || null : null}
              />
            ) : (
              <RunSummary
                operation={operation}
                total={total}
                settledCount={settledCount}
                finished={finished}
                stopped={stopped}
                nothingToDo={nothingToDo}
                failed={failed}
                stopper={stopper}
                result={result}
                selectedCount={selectedCount}
                totalElapsed={totalElapsed}
                lead={lead}
                trace={trace}
              />
            )}
          </div>

          {/* ── footer: what you can do with what the run built ───────────── */}
          <div className="flex flex-none flex-wrap items-center gap-2 px-5 py-[14px] [border-top:1px_solid_var(--border)]">
            {!finished && (
              <span
                className={`inline-flex items-center gap-x-[7px] rounded-[9px] px-3 py-[7px] text-[12.5px] font-semibold ${
                  failed
                    ? 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.12)] dark:text-[#fca5a5]'
                    : 'bg-panel text-dim'
                }`}
              >
                {failed ? (
                  <CloseIcon className="h-[14px] w-[14px]" aria-hidden="true" />
                ) : (
                  <CheckIcon className="h-[14px] w-[14px]" aria-hidden="true" />
                )}
                {nothingToDo ? 'Nothing to do — nothing built' : failed ? 'Stopped — nothing built' : 'Nothing was sent'}
              </span>
            )}
            {onRestart && (
              <button
                type="button"
                onClick={onRestart}
                aria-label="Run this operation again"
                className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[8px] bg-transparent px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink [border:1px_solid_var(--border)]"
              >
                <RefreshIcon className="h-[14px] w-[14px]" />
                Run again
              </button>
            )}
          </div>
        </aside>
      </div>
    </section>
  )
}
